import type { TrackData } from '../track/types';

/**
 * The circuit as a line drawing (SVG), the way a race programme prints it:
 * the track, the start/finish line across it, and the day's showpiece (the
 * corkscrew, the banking…) picked out. Only numbers go into the markup.
 */
export function trackOutlineSvg(t: TrackData): string {
  const n = t.n;
  const every = Math.max(1, Math.round(n / 280));
  const X = (i: number): number => t.px[i];
  // Screen y runs down, the circuit's y runs up.
  const Y = (i: number): number => -t.py[i];
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (let i = 0; i < n; i++) {
    minX = Math.min(minX, X(i));
    maxX = Math.max(maxX, X(i));
    minY = Math.min(minY, Y(i));
    maxY = Math.max(maxY, Y(i));
  }
  const span = Math.max(maxX - minX, maxY - minY);
  const pad = span * 0.08;
  const box = [minX - pad, minY - pad, maxX - minX + 2 * pad, maxY - minY + 2 * pad].map((v) => v.toFixed(1)).join(' ');
  const at = (i: number): string => `${X(i).toFixed(1)},${Y(i).toFixed(1)}`;

  const ring: string[] = [];
  for (let i = 0; i < n; i += every) ring.push(at(i));
  const road = `M${ring.join(' L')} Z`;

  // The showpiece: the stretch named after the day's signature.
  let show = '';
  const sec = t.signature ? t.sections?.find((q) => q.name === t.signature) : undefined;
  if (sec) {
    const inside = (s: number): boolean => (sec.from <= sec.to ? s >= sec.from && s <= sec.to : s >= sec.from || s <= sec.to);
    const first = t.s.findIndex((s) => inside(s));
    const run: string[] = [];
    for (let k = 0; first >= 0 && k < n; k++) {
      const i = (first + k) % n;
      if (!inside(t.s[i])) break;
      if (k % every === 0) run.push(at(i));
    }
    if (run.length > 1) show = `M${run.join(' L')}`;
  }

  // The start/finish line, square across the track.
  const x0 = X(0);
  const y0 = Y(0);
  const nx = -t.ty[0];
  const ny = -t.tx[0];
  const h = span * 0.05;
  const line = `M${(x0 - nx * h).toFixed(1)},${(y0 - ny * h).toFixed(1)} L${(x0 + nx * h).toFixed(1)},${(y0 + ny * h).toFixed(1)}`;

  return `<svg viewBox="${box}" preserveAspectRatio="xMidYMid meet" aria-hidden="true"><path class="road" d="${road}"/>${
    show ? `<path class="show" d="${show}"/>` : ''
  }<path class="line" d="${line}"/></svg>`;
}
